/**
 * `/gallery` — the component library, organised by the five canonical surfaces that define the design standard
 * (owner directive, 2026-08-22): the Dashboard, the folded sidebar, the header bar, the AI Briefing and Session Setup.
 * Every entry names the surface and the reference line it was taken from, so the standard is reviewable at its source.
 * Screens that are not yet refined must conform to this kit — not the other way round.
 */
import { useState } from 'react';
import {
  AccentButton,
  AccountButton,
  Avatar,
  BodyText,
  BrandTile,
  Card,
  CardSubtitle,
  CardTitle,
  CheckRow,
  Chip,
  ComposerShell,
  DashedButton,
  DegradedBanner,
  Dialog,
  Divider,
  EmptyState,
  FailedState,
  FieldLabel,
  FooterRow,
  GhostButton,
  HeaderBar,
  HeaderDivider,
  HeaderIconButton,
  HeaderPill,
  HeadlineFigure,
  IconButton,
  InfoDot,
  Inset,
  KeyTag,
  Legend,
  LegendSwatch,
  ListRow,
  LoadingSkeleton,
  MenuLink,
  MenuPanel,
  Meter,
  ModeButton,
  ModeSwitch,
  MoodTile,
  MutedNote,
  NavRow,
  OutlineChip,
  PagerButton,
  PermissionDenied,
  PlanChip,
  RailBrand,
  RailButton,
  RailColumn,
  RailDivider,
  RailFoldButton,
  RailFooter,
  RailSlab,
  RailSpacer,
  RailViewport,
  RangeSlider,
  RemovableChip,
  SearchField,
  SectionLabel,
  SectionRule,
  Segmented,
  SegmentedOption,
  SessionChip,
  SimulatedBadge,
  StatTile,
  StatusChip,
  TabularCell,
  TextArea,
  TextField,
  Toast,
  Toggle,
  css,
} from '@pointfive/ui';

const RAIL = { bg: 'var(--rail)', fg: 'var(--rail-ink)', fw: '600' };
const IDLE = { bg: 'transparent', fg: 'var(--muted)', fw: '500' };
const ON = { bd: 'color-mix(in srgb, var(--acc-fill) 50%, transparent)', bg: 'var(--l6)', fg: 'var(--text)' };
const OFF = { bd: 'var(--l10)', bg: 'transparent', fg: 'var(--muted)' };
const TOG_ON = { trackBg: 'var(--pos)', knob: 'translateX(14px)', knobBg: 'var(--surface)' };
const TOG_OFF = { trackBg: 'var(--l7)', knob: 'translateX(0px)', knobBg: 'var(--muted2)' };

function Entry({
  name,
  line,
  states,
  children,
}: {
  name: string;
  line: string;
  states?: string;
  children: React.ReactNode;
}) {
  return (
    <Card padding="dense" extra="display:flex;flex-direction:column;gap:11px;min-width:0">
      <div style={css('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap')}>
        <span style={css('font-size:13px;font-weight:500')}>{name}</span>
        <span style={css('font-size:10.5px;color:var(--faint)')}>{line}</span>
      </div>
      <div
        data-gallery-group={name}
        style={css('display:flex;align-items:center;gap:14px;flex-wrap:wrap;min-height:34px')}
      >
        {children}
      </div>
      {states ? (
        <FooterRow>
          <span
            style={css(
              'font-size:9.5px;letter-spacing:0.11em;text-transform:uppercase;color:var(--muted2);font-weight:600',
            )}
          >
            {states}
          </span>
        </FooterRow>
      ) : null}
    </Card>
  );
}
function Surface({
  id,
  title,
  blurb,
  children,
}: {
  id: string;
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <section data-gallery-surface={id} style={css('display:flex;flex-direction:column;gap:12px')}>
      <div>
        <SectionLabel>{title}</SectionLabel>
        <MutedNote extra="max-width:80ch">{blurb}</MutedNote>
      </div>
      <div
        style={css(
          'display:grid;grid-template-columns:repeat(auto-fit,minmax(420px,1fr));gap:12px;align-items:start',
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function GalleryPage() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [seg, setSeg] = useState('Week');
  const [chip, setChip] = useState('Patience');
  const [mood, setMood] = useState('good');
  const [on, setOn] = useState(true);
  const [energy, setEnergy] = useState(6);
  const [note, setNote] = useState('');
  const [dialog, setDialog] = useState(false);
  const [toast, setToast] = useState(false);
  const [menu, setMenu] = useState(false);
  return (
    <div
      data-page="gallery"
      data-theme={theme}
      style={css('min-height:100vh;background:var(--bg);color:var(--text);font-size:13px')}
    >
      <div
        style={css(
          'max-width:1440px;margin:0 auto;padding:24px 12px 40px;display:flex;flex-direction:column;gap:20px',
        )}
      >
        <div style={css('display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap')}>
          <div style={css('min-width:0')}>
            <SectionLabel>Component library</SectionLabel>
            <div style={css('font-size:15px;font-weight:500;letter-spacing:-0.01em;margin-top:7px')}>
              The design standard, at its source
            </div>
            <BodyText extra="margin-top:6px">
              The standard is set by five surfaces of the design reference — the Dashboard, the folded
              sidebar, the header bar, the AI Briefing and Session Setup. Every component below is derived
              from one of them and emits its literal verbatim, so the screens compose from these without any
              visual change. Screens still being refined conform to this kit, not the reverse.
            </BodyText>
          </div>
          <div style={css('flex:1')} />
          <Segmented>
            {(['light', 'dark'] as const).map((t) => (
              <SegmentedOption
                key={t}
                {...(theme === t ? RAIL : IDLE)}
                onClick={() => setTheme(t)}
                aria-label={`${t} theme`}
              >
                {t === 'light' ? 'Linen' : 'Obsidian'}
              </SegmentedOption>
            ))}
          </Segmented>
        </div>

        <Surface
          id="header"
          title="1 · Header bar"
          blurb="Reference lines 205–249. The app's chrome vocabulary: hairline pills, the rail-ground mode capsule, session chips, the bell with its count badge and the account menu."
        >
          <Entry name="HeaderBar" line="line 205 · 52px, radius 18, surface + card shadow">
            <div style={css('width:100%;background:var(--bg);border-radius:18px')}>
              <HeaderBar>
                <HeaderPill icon="ph-funnel">Symbols</HeaderPill>
                <HeaderPill icon="ph-calendar-blank">All Time</HeaderPill>
                <div style={css('flex:1')} />
                <HeaderDivider />
                <HeaderIconButton icon="ph-bell" badge="3" aria-label="Notifications" />
              </HeaderBar>
            </div>
          </Entry>
          <Entry
            name="ModeSwitch"
            line="lines 212–213 · rail capsule + mode buttons"
            states="active (labelled) · idle (icon only)"
          >
            <div style={css('position:relative;height:44px;width:100%')}>
              <ModeSwitch>
                <ModeButton
                  icon="ph-sun"
                  pad="4px 15px 4px 4px"
                  bg="var(--rail-on)"
                  fg="var(--rail-ink)"
                  fw="600"
                >
                  Prepare
                </ModeButton>
                <ModeButton
                  icon="ph-arrows-left-right"
                  pad="4px"
                  bg="transparent"
                  fg="var(--rail-dim)"
                  aria-label="Execute"
                />
                <ModeButton
                  icon="ph-book-open"
                  pad="4px"
                  bg="transparent"
                  fg="var(--rail-dim)"
                  aria-label="Review"
                />
              </ModeSwitch>
            </div>
          </Entry>
          <Entry name="SessionChip" line="line 219 · market sessions" states="open · secondary · closed">
            <SessionChip
              dot="var(--amb)"
              bd="color-mix(in srgb, var(--amb) 40%, transparent)"
              fg="var(--amb)"
              bg="color-mix(in srgb, var(--amb) 8%, transparent)"
              name="LON"
              time="1:59"
            />
            <SessionChip
              dot="var(--pos)"
              bd="var(--l8)"
              fg="var(--text-mid)"
              bg="transparent"
              name="NYC"
              time="8:59"
            />
            <SessionChip dot="var(--ghost)" bd="var(--l8)" fg="var(--muted)" bg="transparent" name="TYO" />
          </Entry>
          <Entry name="Account menu" line="lines 228–245 · button, avatar, popover" states="closed · open">
            <div style={css('position:relative')}>
              <AccountButton onClick={() => setMenu(!menu)} aria-label="Account">
                <Avatar size={30} initials="ON" />
                <i className="ph ph-caret-down" style={css('font-size:11px;color:var(--muted2)')}></i>
              </AccountButton>
              {menu ? (
                <MenuPanel>
                  <div style={css('display:flex;align-items:center;gap:10px')}>
                    <Avatar size={38} initials="ON" />
                    <div style={css('min-width:0')}>
                      <div style={css('font-size:13px;font-weight:600')}>Omichael Nhamburo</div>
                      <div style={css('font-size:11px;color:var(--muted)')}>@omichael</div>
                    </div>
                  </div>
                  <Divider />
                  <div style={css('display:flex;align-items:center;gap:8px')}>
                    <FieldLabel>Plan</FieldLabel>
                    <div style={css('flex:1')} />
                    <PlanChip>PRO · ANNUAL</PlanChip>
                  </div>
                  <Divider />
                  <MenuLink icon="ph-gear-six">Settings</MenuLink>
                  <MenuLink icon="ph-sign-out">Sign out</MenuLink>
                </MenuPanel>
              ) : null}
            </div>
          </Entry>
        </Surface>

        <Surface
          id="rail"
          title="2 · Folded sidebar (the rail)"
          blurb="Reference lines 147–173. The dark-rail vocabulary: brand tile, the scrolling slab with its edge fades, 38px nav buttons, the vertical mode spacer and the soft footer. Its expanded companion is lines 174–204."
        >
          <Entry name="Rail" line="lines 148–172 · the whole 68px column" states="active · idle">
            <div style={css('height:360px;display:flex;background:var(--bg)')}>
              <RailColumn>
                <RailBrand />
                <RailSlab>
                  <RailFoldButton
                    icon="ph-caret-right"
                    title="Expand navigation"
                    aria-label="Expand navigation"
                  />
                  <RailDivider />
                  <RailViewport>
                    <RailButton
                      icon="ph-house"
                      bg="var(--rail-on)"
                      fg="var(--rail-ink)"
                      aria-label="Dashboard"
                    />
                    <RailButton
                      icon="ph-newspaper"
                      bg="transparent"
                      fg="var(--rail-dim)"
                      aria-label="News Brief"
                    />
                    <RailButton
                      icon="ph-strategy"
                      bg="transparent"
                      fg="var(--rail-dim)"
                      aria-label="Strategies"
                    />
                    <RailButton
                      icon="ph-shield"
                      bg="transparent"
                      fg="var(--rail-dim)"
                      aria-label="Risk Rules"
                    />
                  </RailViewport>
                </RailSlab>
                <RailSpacer label="Prepare" icon="ph-sun" />
                <RailFooter>
                  <RailButton
                    icon="ph-moon-stars"
                    bg="transparent"
                    fg="var(--muted)"
                    aria-label="Toggle theme"
                  />
                  <RailButton
                    icon="ph-gear-six"
                    bg="var(--rail)"
                    fg="var(--rail-ink)"
                    aria-label="Settings"
                  />
                </RailFooter>
              </RailColumn>
            </div>
          </Entry>
          <Entry name="NavRow (expanded)" line="line 186 · sidebar row" states="active · idle · with count">
            <div style={css('width:250px;display:flex;flex-direction:column;gap:2px')}>
              <NavRow
                icon="ph-house"
                label="Dashboard"
                bd="var(--row-on-line)"
                bg="var(--row-on)"
                fg="var(--text)"
                fw="600"
                ic="var(--text)"
              />
              <NavRow
                icon="ph-shield"
                label="Risk Rules"
                count="3"
                bd="transparent"
                bg="transparent"
                fg="var(--text-mid)"
                ic="var(--muted)"
              />
              <NavRow
                icon="ph-strategy"
                label="Strategies"
                bd="transparent"
                bg="transparent"
                fg="var(--text-mid)"
                ic="var(--muted)"
              />
            </div>
          </Entry>
          <Entry name="BrandTile" line="line 177 · expanded sidebar brand">
            <BrandTile />
          </Entry>
        </Surface>

        <Surface
          id="dashboard"
          title="3 · Dashboard"
          blurb="Reference lines 252–496. The card grammar, the section label, the segmented control that is the app's primary selection signal, headline figures, legends and the calendar pager."
        >
          <Entry name="Card" line="lines 253, 396 · surface + padding" states="dense · roomy · flat · tall">
            {(['dense', 'roomy', 'flat', 'tall'] as const).map((p) => (
              <Card key={p} padding={p} extra="min-width:120px">
                <FieldLabel>{p}</FieldLabel>
                <div style={css('font-size:12.5px;margin-top:4px')}>Card surface</div>
              </Card>
            ))}
          </Entry>
          <Entry
            name="Segmented"
            line="line 259 · five declaration-order variants"
            states="active (rail-dark) · idle"
          >
            <Segmented>
              {['Day', 'Week', 'Month', 'Year', 'All Time'].map((l) => (
                <SegmentedOption key={l} {...(seg === l ? RAIL : IDLE)} onClick={() => setSeg(l)}>
                  {l}
                </SegmentedOption>
              ))}
            </Segmented>
          </Entry>
          <Entry name="SectionLabel · HeadlineFigure" line="lines 256–257" states="28px hero · 22px card">
            <StatTile label="Net P&L" value="$542,692.00" sub="17 trades" size={28} />
            <HeadlineFigure size={22} color="var(--pos)">
              +$15,676
            </HeadlineFigure>
          </Entry>
          <Entry name="Legend · PagerButton · InfoDot" line="lines 442, 460, 494" states="enabled · disabled">
            <Legend
              swatch={
                <LegendSwatch
                  bg="color-mix(in srgb, var(--pos) 15%, transparent)"
                  bd="color-mix(in srgb, var(--pos) 50%, transparent)"
                />
              }
            >
              Winning day
            </Legend>
            <Legend
              swatch={
                <LegendSwatch
                  bg="transparent"
                  bd="var(--l6)"
                  img="repeating-linear-gradient(45deg,var(--l10) 0 2px,transparent 2px 5px)"
                />
              }
            >
              No trading
            </Legend>
            <PagerButton icon="ph-caret-left" aria-label="Previous month" />
            <PagerButton
              icon="ph-caret-right"
              color="var(--ghost)"
              cursor="default"
              aria-label="Latest month"
            />
            <InfoDot title="Win rate = winning closed trades ÷ closed trades" />
          </Entry>
          <Entry name="Meter" line="§9.4 — the scale derives from the value" states="high · mid · low">
            {[
              ['82%', 'var(--pos)'],
              ['48%', 'var(--amb)'],
              ['12%', 'var(--neg)'],
            ].map(([f, c]) => (
              <div key={f} style={css('width:140px')}>
                <Meter fill={f!} color={c} />
              </div>
            ))}
          </Entry>
        </Surface>

        <Surface
          id="briefing"
          title="4 · AI Briefing"
          blurb="Reference lines 730–800. The reading surface: outlined insets, status and outline chips, removable watchlist chips, the fading section rule and calendar list rows."
        >
          <Entry name="Inset" line="line 745 (outline) · line 869 (filled)" states="outline · filled">
            <Inset extra="min-width:180px">
              <FieldLabel>Outline</FieldLabel>
              <div style={css('font-size:12px;margin-top:5px')}>Briefing inset</div>
            </Inset>
            <Inset filled extra="min-width:180px">
              <FieldLabel>Filled</FieldLabel>
              <div style={css('font-size:12px;margin-top:5px')}>Session Setup inset</div>
            </Inset>
          </Entry>
          <Entry
            name="StatusChip · OutlineChip"
            line="lines 743–744"
            states="positive · negative · neutral · outline"
          >
            <StatusChip bg="color-mix(in srgb, var(--pos) 12%, transparent)" fg="var(--pos)">
              BULLISH
            </StatusChip>
            <StatusChip bg="color-mix(in srgb, var(--neg-fill) 14%, transparent)" fg="var(--neg)">
              BEARISH
            </StatusChip>
            <StatusChip bg="var(--l8)" fg="var(--muted)">
              NEUTRAL
            </StatusChip>
            <OutlineChip>WATCHING</OutlineChip>
          </Entry>
          <Entry name="RemovableChip · SearchField" line="lines 741, 605" states="default · with value">
            <RemovableChip label="Remove XAUUSD">XAUUSD</RemovableChip>
            <RemovableChip label="Remove EURUSD">EURUSD</RemovableChip>
            <SearchField placeholder="Add symbol…" aria-label="Add symbol" />
          </Entry>
          <Entry name="SectionRule · ListRow" line="lines 611, 786">
            <div style={css('width:100%')}>
              <SectionRule />
              <ListRow>
                <TabularCell width={40}>08:30</TabularCell>
                <KeyTag>USD</KeyTag>
                <span style={css('font-size:11.5px;flex:1')}>Initial Jobless Claims</span>
                <span style={css('font-size:10px;font-weight:500;color:var(--neg)')}>High</span>
              </ListRow>
              <ListRow last>
                <TabularCell width={40}>09:45</TabularCell>
                <KeyTag>USD</KeyTag>
                <span style={css('font-size:11.5px;flex:1')}>S&P Global Flash PMI</span>
                <span style={css('font-size:10px;font-weight:500;color:var(--amb-2)')}>Medium</span>
              </ListRow>
            </div>
          </Entry>
        </Surface>

        <Surface
          id="session-setup"
          title="5 · Session Setup"
          blurb="Reference lines 801–930. The form vocabulary: card title with its mode-accent icon, field labels, text fields and areas, the energy range, mood tiles, selection chips and checklist rows."
        >
          <Entry name="CardTitle · CardSubtitle" line="lines 808–809">
            <div style={css('min-width:260px')}>
              <CardTitle icon="ph-brain">Mental State</CardTitle>
              <CardSubtitle>How are you feeling before the session?</CardSubtitle>
            </div>
          </Entry>
          <Entry name="MoodTile" line="line 819" states="selected · unselected">
            {[
              ['excellent', 'ph-thumbs-up', 'Excellent'],
              ['good', 'ph-smiley', 'Good'],
              ['neutral', 'ph-smiley-meh', 'Neutral'],
            ].map(([id, icon, label]) => (
              <MoodTile
                key={id}
                icon={icon!}
                label={label}
                bd={mood === id ? 'color-mix(in srgb, var(--acc-fill) 50%, transparent)' : 'var(--l10)'}
                bg={mood === id ? 'var(--l6)' : 'var(--surface)'}
                fg={mood === id ? 'var(--text)' : 'var(--muted)'}
                onClick={() => setMood(id!)}
                aria-pressed={mood === id}
              />
            ))}
          </Entry>
          <Entry name="RangeSlider" line="line 823 · --fill drives the track">
            <div style={css('width:240px')}>
              <div style={css('font-size:11.5px;color:var(--text-mid)')}>Energy Level: {energy}/10</div>
              <RangeSlider
                value={energy}
                fill={`${energy * 10}%`}
                onChange={(e) => setEnergy(+e.target.value)}
                aria-label="Energy level"
              />
            </div>
          </Entry>
          <Entry name="TextField · TextArea" line="lines 826, 833, 891" states="default · tabular · roomy">
            <div style={css('width:180px')}>
              <FieldLabel nowrap>Max trades</FieldLabel>
              <TextField value="5" tabular readOnly aria-label="Max trades" />
            </div>
            <div style={css('width:240px')}>
              <FieldLabel nowrap>Daily goal</FieldLabel>
              <TextField placeholder="What would a good day look like?" roomy aria-label="Daily goal" />
            </div>
            <div style={css('width:100%')}>
              <TextArea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Pre-session notes…"
                aria-label="Pre-session notes"
              />
            </div>
          </Entry>
          <Entry name="Chip" line="line 842 · focus & strategy chips" states="on · off">
            {['Patience', 'Discipline', 'Risk Management'].map((l) => (
              <Chip key={l} {...(chip === l ? ON : OFF)} onClick={() => setChip(l)} aria-pressed={chip === l}>
                {l}
              </Chip>
            ))}
          </Entry>
          <Entry name="CheckRow · Toggle" line="lines 860, 874, 945" states="active · off · interactive">
            <div style={css('width:100%;display:flex;flex-direction:column')}>
              <CheckRow
                icon="ph-check-circle"
                iconColor="var(--pos)"
                label="Breakeven at 1R"
                status="Active"
                statusColor="var(--pos)"
              />
              <CheckRow
                icon="ph-circle"
                iconColor="var(--ghost)"
                label="Time-Based Exit (4 hours)"
                labelColor="var(--muted2)"
                status="Off"
                statusColor="var(--faint)"
              />
              <div style={css('display:flex;align-items:center;gap:10px;padding:7px 0')}>
                <span style={css('font-size:11.5px;flex:1')}>Trailing Stop (15 pips)</span>
                <Toggle
                  {...(on ? TOG_ON : TOG_OFF)}
                  onClick={() => setOn(!on)}
                  aria-label="Trailing stop"
                  aria-pressed={on}
                />
              </div>
            </div>
          </Entry>
          <Entry name="Buttons" line="lines 806, 897, 916" states="ghost · accent · dashed · icon">
            <GhostButton icon="ph-clock-counter-clockwise">History</GhostButton>
            <AccentButton icon="ph-play">Start session</AccentButton>
            <AccentButton icon="ph-check" tone="var(--pos)" ink="var(--pos)">
              Confirm
            </AccentButton>
            <DashedButton>+ Add rule</DashedButton>
            <IconButton icon="ph-pencil-simple" aria-label="Edit" />
            <IconButton icon="ph-trash" aria-label="Delete" disabled />
          </Entry>
        </Surface>

        <Surface
          id="chrome"
          title="App chrome & the simulated indicator"
          blurb="The composer shell, the confirm dialog and the toast (reference lines 4003, 3916, 3987), plus the one indicator that marks every simulated integration (ground rule §3.2)."
        >
          <Entry name="ComposerShell" line="line 4003 · ⌘K panel">
            <div style={css('transform:scale(0.85);transform-origin:top left')}>
              <ComposerShell onClose={() => undefined}>
                <div style={css('padding:13px 15px')}>
                  <TextField
                    placeholder="Analyse my XAUUSD trades…"
                    extra="margin-top:0"
                    aria-label="Ask PointFive"
                  />
                </div>
              </ComposerShell>
            </div>
          </Entry>
          <Entry name="Dialog · Toast" line="lines 3916, 3987" states="open">
            <GhostButton onClick={() => setDialog(true)}>Show dialog</GhostButton>
            <GhostButton onClick={() => setToast(!toast)}>Toggle toast</GhostButton>
          </Entry>
          <Entry name="SimulatedBadge" line="ground rule §3.2" states="default · compact">
            <SimulatedBadge what="broker" />
            <SimulatedBadge what="market data" />
            <SimulatedBadge what="AI" compact />
          </Entry>
        </Surface>

        <Surface
          id="states"
          title="The five standard states"
          blurb="SDD §7.4 inherited baseline / §9.3 — every screen renders these from the library, never ad hoc. The grammar is the reference's own empty states (lines 3268, 1379)."
        >
          <Entry name="LoadingSkeleton" line="§7.4 loading">
            <div style={css('width:220px')}>
              <LoadingSkeleton />
            </div>
          </Entry>
          <Entry name="EmptyState" line="lines 3268, 1379 · empty with guidance">
            <div style={css('width:100%')}>
              <EmptyState
                title="No trades match these filters"
                guidance="Widen the range or clear a filter."
              />
            </div>
          </Entry>
          <Entry name="DegradedBanner" line="§8.2 · last-good timestamp">
            <div style={css('width:100%')}>
              <DegradedBanner source="TradeLocker sync" lastGood="15:59:32" onRetry={() => undefined} />
            </div>
          </Entry>
          <Entry name="PermissionDenied" line="§17.3 · names the tier">
            <div style={css('width:100%')}>
              <PermissionDenied feature="Investor portal" requires="Pro" onUpgrade={() => undefined} />
            </div>
          </Entry>
          <Entry name="FailedState" line="§7.4 · scoped retry">
            <div style={css('width:100%')}>
              <FailedState
                what="The equity curve"
                detail="The analytics aggregate did not respond."
                onRetry={() => undefined}
              />
            </div>
          </Entry>
        </Surface>
      </div>

      {dialog ? (
        <Dialog label="Gallery dialog" onScrimClick={() => setDialog(false)}>
          <div style={css('font-size:13.5px;font-weight:500')}>Confirm BUY order</div>
          <CardSubtitle>The dialog panel is the card surface at float elevation.</CardSubtitle>
          <div style={css('display:flex;gap:9px;margin-top:14px')}>
            <GhostButton onClick={() => setDialog(false)}>Cancel</GhostButton>
            <AccentButton tone="var(--pos)" ink="var(--pos)" onClick={() => setDialog(false)}>
              Send order
            </AccentButton>
          </div>
        </Dialog>
      ) : null}
      {toast ? (
        <Toast icon="ph-check-circle" iconColor="var(--pos)">
          Bought 0.01 XAUUSD at 4,036.59
        </Toast>
      ) : null}
    </div>
  );
}
